import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import UsageAssignmentCard from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard';
import UsageSettingsSidebar from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar';
import type AiModelsListView from '@UI/Store/AiConfiguration/Views/AiModelsListView';
import type ModelAssignmentsListView from '@UI/Store/AiConfiguration/Views/ModelAssignmentsListView';
import { css } from '@emotion/react';
import * as React from 'react';

/**
 * Columns only: each `UsageAssignmentCard` subgrids the row axis onto this grid (`grid-row: span 4`,
 * a fixed track count the card itself normalizes across its render branches) so that cards sharing a
 * visual row settle on the same height, block for block.
 */
const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
	gap: 1.25rem;
`;

/** Names the active screen's own level, for the card's "Défini pour…" phrasing. */
const SCOPE_LABELS: Record<AssignmentScope['type'], string> = {
	account: 'ce compte',
	organization: 'cette structure',
	facility: 'cette agence',
};

type Props = {
	readonly scope: AssignmentScope;
	readonly assignments: ModelAssignmentsListView;
	readonly models: AiModelsListView;
};

/**
 * The grid of usage cards shared by the three affectation screens (account, organization, facility),
 * at a `scope` away from being identical: three copies would be three places to fix the next card.
 * Callers own their own loading/error states and only render this once `assignments` and `models` are
 * both ready — this component itself never shows a skeleton.
 */
const UsageAssignmentsBoard: React.FC<Props> = ({ scope, assignments, models }) => {
	return (
		<React.Fragment>
			<section>
				<div css={cardsGridStyle}>
					{assignments.list.map((assignment) => (
						<UsageAssignmentCard
							key={assignment.usage.value}
							assignment={assignment}
							scope={scope}
							scopeLabel={SCOPE_LABELS[scope.type]}
							models={models}
						/>
					))}
				</div>
			</section>
			<UsageSettingsSidebar scope={scope} assignments={assignments} />
		</React.Fragment>
	);
};

export default UsageAssignmentsBoard;
